import * as React from 'react';
import Typography from 'components/Typography';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import useStyles from '../fundedInvestingManualKycDialog.styles';
import { useTheme } from '@material-ui/core';
import SumsubWebSdk from '@sumsub/websdk-react';
import { mixPanelEventsTypes } from 'helpers/mixPanel';
import { AccessTokenResponse, AccountInfoParsed } from 'services/generatedClientFromSwagger/models';
import { user_userInfo } from 'store/user/selectors';
import { sumsub_getSumsubData, sumsub_requestApproved } from 'features/sumsub/store/actions';
import { sumsub_loadingState, sumsub_subsubData } from 'features/sumsub/store/selector';
import analyticsManager from 'helpers/analyticsManager';
import Loader from 'components/Loader';
import Button from 'components/Button';

export interface SumSubDialogStepProps {
    subsubData: AccessTokenResponse | undefined;
    loading: boolean;
    userInfo: AccountInfoParsed;
    uploadD_getSumsubData: () => Promise<string>;
    uploadD_requestApproved: () => void;
    onSumSubSubmission: () => void;
}

const mapStateToProps = (state) => ({
    subsubData: sumsub_subsubData(state),
    loading: sumsub_loadingState(state),
    userInfo: user_userInfo(state),
});

const mapDispatchToProps = {
    uploadD_getSumsubData: sumsub_getSumsubData,
    uploadD_requestApproved: sumsub_requestApproved,
};

const SumSubDialogStep: React.FunctionComponent<SumSubDialogStepProps> = (props) => {
    const { subsubData, userInfo, uploadD_getSumsubData, uploadD_requestApproved, onSumSubSubmission } = props;
    const {
        t,
        i18n: { language },
    } = useTranslation('prop');
    const classes = useStyles();
    const theme = useTheme();

    // The SDK builds its iframe once on mount and ignores later prop changes, so it must only mount with the
    // token fetched for this open: a placeholder token loads the removed WebSDK 1.0 page (/idensic/websdk.html)
    // and a token left in the store from a previous open may be stale.
    const [sdkToken, setSdkToken] = React.useState<string>();
    const [tokenFailed, setTokenFailed] = React.useState(false);
    const isMounted = React.useRef(true);

    React.useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const loadToken = React.useCallback(() => {
        setTokenFailed(false);
        Promise.resolve(uploadD_getSumsubData())
            .catch(() => undefined)
            .then((token) => {
                if (!isMounted.current) return;
                if (token) setSdkToken(token);
                else setTokenFailed(true);
            });
    }, [uploadD_getSumsubData]);

    React.useEffect(() => {
        loadToken();
    }, [loadToken]);

    // The thunk resolves undefined on failure; reject instead so the SDK reports the failed refresh as an error
    // rather than posting an undefined token to the widget.
    const refreshToken = React.useCallback(
        () =>
            Promise.resolve(uploadD_getSumsubData()).then((token) => {
                if (!token) throw new Error('Failed to refresh Sumsub access token');
                return token;
            }),
        [uploadD_getSumsubData]
    );

    const messageHandler = (type, payload) => {
        analyticsManager.trackEvent(mixPanelEventsTypes.SUMSUB_EVENTS, { type, payload });
        console.log(type, payload);
        if (type === 'idCheck.onApplicantSubmitted' || type === 'idCheck.onApplicantResubmitted') {
            uploadD_requestApproved();
            onSumSubSubmission();
            // setRenewPlatformUserToSS();
        }
    };

    const errorHandler = (error) => {
        analyticsManager.trackEvent(mixPanelEventsTypes.SUMSUB_EVENTS, { type: 'error', error });
        console.error('WebSDK onError', error);
    };

    return (
        <>
            <Typography className={classes.sumSubTitle} variant="tLgSemibold" color="inherit">
                {t('checkout.sumSubDialog.title')}
            </Typography>
            <div className={classes.center}>
                {!sdkToken && !tokenFailed && <Loader />}
                {!sdkToken && tokenFailed && (
                    <>
                        <Typography variant="tSmallRegular" color="inherit">
                            {t('newChallengeDepositSuccess.failure.info')}
                        </Typography>
                        <Button className={classes.btn} variant="contained" color="primary" onClick={loadToken}>
                            {t('newChallengeDepositSuccess.failure.tryAgain')}
                        </Button>
                    </>
                )}
                {sdkToken && (
                    <SumsubWebSdk
                        accessToken={sdkToken}
                        expirationHandler={refreshToken}
                        config={{
                            lang: language, //language of WebSDK texts and comments (ISO 639-1 format)
                            email: userInfo.email || undefined,
                            theme: theme.palette.type,
                        }}
                        baseUrl={subsubData?.apiUrl}
                        options={{ addViewportTag: false, adaptIframeHeight: true }}
                        onMessage={messageHandler}
                        onError={errorHandler}
                    />
                )}
            </div>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(React.memo(SumSubDialogStep));
